import React from "react";
import ReactDOM from "react-dom/client";
import "@/index.css";
import App from "@/App";
import { registerServiceWorker, applyServiceWorkerUpdate } from "@/lib/pwa";
import { isNativeApp } from "@/lib/platform";

// =============================================================================
// Suppress the harmless "ResizeObserver loop completed with undelivered
// notifications" warning. Browsers fire this whenever a ResizeObserver
// callback re-measures layout twice in one frame — it is purely cosmetic but
// Create React App's dev error overlay catches it and paints a red screen.
//
// Two-prong fix:
//  1. Wrap window.ResizeObserver so every callback runs inside
//     requestAnimationFrame. This breaks the synchronous loop the browser
//     complains about, preventing the warning at the source for any
//     library we use (Radix, Recharts, etc.).
//  2. Still capture window 'error' events with this specific message in the
//     CAPTURE phase as a belt-and-braces fallback (older browsers may still
//     emit the warning under certain conditions).
// =============================================================================
const RESIZE_OBSERVER_ERR = "ResizeObserver loop";

if (typeof window !== "undefined" && typeof window.ResizeObserver !== "undefined") {
  const OriginalRO = window.ResizeObserver;
  // Avoid double-wrapping under React StrictMode hot reload.
  if (!OriginalRO.__patchedForOverlay) {
    class PatchedResizeObserver extends OriginalRO {
      constructor(callback) {
        super((entries, observer) => {
          // Defer the user callback so the browser finishes its layout
          // pass before we touch the DOM again. This prevents the loop
          // warning while preserving correct resize notifications.
          window.requestAnimationFrame(() => {
            try {
              callback(entries, observer);
            } catch (err) {
              // Swallow ResizeObserver-loop errors only; rethrow others.
              if (!(err && typeof err.message === "string" && err.message.includes(RESIZE_OBSERVER_ERR))) {
                throw err;
              }
            }
          });
        });
      }
    }
    PatchedResizeObserver.__patchedForOverlay = true;
    window.ResizeObserver = PatchedResizeObserver;
  }
}

// Capture-phase listeners so we fire before CRA's overlay error handler.
const swallowResizeObserverError = (event) => {
  const msg = event.message || (event.reason && event.reason.message) || "";
  if (typeof msg === "string" && msg.includes(RESIZE_OBSERVER_ERR)) {
    event.stopImmediatePropagation();
    if (typeof event.preventDefault === "function") event.preventDefault();
    return false;
  }
  return undefined;
};
window.addEventListener("error", swallowResizeObserverError, true);
window.addEventListener("unhandledrejection", swallowResizeObserverError, true);

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <App />
  </React.StrictMode>,
);

// PWA: register the service worker after the app has mounted. When an
// update is detected we stash the registration on `window.__nfcsUpdate`
// AND fire a CustomEvent so the running React app can show its toast.
// We avoid an auto-reload — kitchen tablets mid-service shouldn't lose
// state because a small patch landed. Users opt-in via the toast.
//
// SKIPPED under Capacitor: the APK ships the built assets inside its
// own bundle and updates via the self-hosted APK / Capgo pipelines
// (see /docs/OTA_UPDATES.md). Running a Service Worker on top of the
// native WebView adds no value and can double-cache resources.
if (!isNativeApp()) {
  registerServiceWorker({
    onUpdateReady: (registration) => {
      try {
        window.__nfcsUpdate = {
          registration,
          apply: () => applyServiceWorkerUpdate(registration),
        };
        window.dispatchEvent(new CustomEvent("nfcs:update-ready"));
      } catch (_) { /* noop */ }
    },
  });
}
